// Pure helpers behind the VFI badge — kept outside the component so they
// are straightforward to unit-test without rendering React.

// Tailwind-equivalent palette.
export const VFI_GREEN = '#22C55E';
export const VFI_AMBER = '#F59E0B';
export const VFI_RED   = '#EF4444';

/**
 * Classify a VFI scalar into the spec's three tiers.
 *
 * The spec stores VFI as a coefficient of variation (stddev/mean) in the
 * [0, 1] range with thresholds 0.3 and 0.6. Some pipelines store it on a
 * 0–100 percentage scale instead — we normalize back to the CV scale so the
 * classification is consistent regardless of which scale reached the UI.
 *
 * @param {number|null|undefined} value value_numeric from aim_metric_snapshots
 * @returns {{cv: number, color: string, severity: string, meterPct: number}}
 */
// SHIM (A2 in AIM data quality report 2026-05-25):
// `fn_aim_compute_metrics` stores `avg_rate` in `value_numeric` for the VFI
// metric instead of the spec's CV (STDDEV_POP(mention_pct)/AVG(mention_pct)).
// Until the writer is fixed and historical rows are backfilled, this normalize
// branch keeps the badge tier correct for snapshots that landed on the 0–100
// avg_rate scale by collapsing them back into the [0, 1] CV scale.
//
// Removal plan (track separately as a DB ticket):
//   1. Fix fn_aim_compute_metrics so value_numeric stores the actual CV.
//   2. Backfill: UPDATE aim_metric_snapshots SET value_numeric = <computed_cv>
//      WHERE metric_code = 'vfi' using the existing value_breakdown to
//      recompute per row.
//   3. After a week of new snapshots arrive with CV-shaped scalars, drop the
//      `num > 1 ? num/100 : num` branch so future regressions surface loudly.
export function classifyVfi(value) {
  const num = Number(value);
  const cv = Number.isFinite(num) ? (num > 1 ? num / 100 : num) : 0;

  let color = VFI_GREEN;
  let severity = 'Low (Consistent)';
  if (cv > 0.6) { color = VFI_RED; severity = 'High (Fragile)'; }
  else if (cv > 0.3) { color = VFI_AMBER; severity = 'Moderate'; }

  const meterPct = Math.min(100, Math.max(0, cv * 100));
  return { cv, color, severity, meterPct };
}

/**
 * Normalize a VFI `value_breakdown` payload into a flat array of category rows.
 *
 * Three shapes have shipped for this field and all three reach the UI:
 *   - Express/MySQL query — `{ avg_rate, strongest_category, …, breakdown: [{ category, pct, mention_pct }] }`
 *   - Supabase RPC        — a bare array `[{ category, mention_rate_pct, mentioned_in, total_responses, deviation_pct }]`
 *   - degraded runs       — `null` / absent
 *
 * Every returned row carries a numeric `pct` resolved from whichever key the
 * payload used, so callers read one key regardless of which stack served it.
 * Unparseable percentages come back as `NaN` for callers to filter.
 *
 * @param {object|Array|null|undefined} breakdown value_breakdown
 * @returns {Array<object>} category rows, each with a numeric `pct`
 */
export function vfiBreakdownRows(breakdown) {
  const rows = Array.isArray(breakdown)
    ? breakdown
    : (Array.isArray(breakdown?.breakdown) ? breakdown.breakdown : []);
  return rows
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({ ...r, pct: Number(r.mention_pct ?? r.pct ?? r.mention_rate_pct) }));
}

/**
 * Compute the VFI per spec §1B from the breakdown payload:
 *   vfi = STDDEV_POP(mention_pct) / AVG(mention_pct)
 *
 * Backend snapshots have been observed to store the wrong scalar in
 * `value_numeric` (e.g., avg_rate instead of the CV). When the breakdown is
 * available, recomputing on the client recovers the correct number.
 *
 * Returns null when the breakdown is missing, has fewer than 2 categories, or
 * has a zero mean (formula undefined).
 *
 * @param {object|Array|null|undefined} breakdown value_breakdown
 * @returns {number|null}
 */
export function computeVfiFromBreakdown(breakdown) {
  const pcts = vfiBreakdownRows(breakdown)
    .map((r) => r.pct)
    .filter((n) => Number.isFinite(n));
  if (pcts.length < 2) return null;
  const mean = pcts.reduce((s, x) => s + x, 0) / pcts.length;
  if (mean === 0) return null;
  const variance = pcts.reduce((s, x) => s + (x - mean) ** 2, 0) / pcts.length;
  return Math.sqrt(variance) / mean;
}

/**
 * Given a VFI value_breakdown payload (spec §1B), return the strongest and
 * worst category entries plus a sorted copy of the array. Accepts every shape
 * `vfiBreakdownRows` handles, so rows always expose a numeric `pct`.
 *
 * @param {object|Array|null|undefined} breakdown value_breakdown
 * @returns {{sorted: Array, best: object|null, worst: object|null}}
 */
export function extractVfiBreakdown(breakdown) {
  const rows = vfiBreakdownRows(breakdown);
  const sorted = [...rows].sort(
    (a, b) => (Number.isFinite(b.pct) ? b.pct : 0) - (Number.isFinite(a.pct) ? a.pct : 0),
  );
  const best = sorted[0] || null;
  // Only return a distinct worst when we have more than one category.
  const worst = sorted.length > 1 ? sorted[sorted.length - 1] : null;
  return { sorted, best, worst };
}
